<template>
  <v-container>
    <v-row v-if="loading">
      <v-col cols="12">
        <v-skeleton-loader
          type="table"
          class="mb-4"
        />
      </v-col>
    </v-row>

    <v-row v-else-if="((deposits) && (deposits.length > 0))">
      <v-col class="ma-3">
        <v-table
          v-if="deposits"
          dense
        >
          <thead>
            <tr>
              <th>Name</th>
              <th>Created date</th>
              <th>Status</th>
              <th v-if="isAdmin">
                Edit
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(metadataFile, index) in deposits"
              :key="index"
            >
              <td>
                <router-link
                  v-if="metadataFile.status === 'open'"
                  title="Access the deposit area"
                  :to="{
                    name: 'DepositFileHome',
                    params: { deposit_id: metadataFile.id },
                    query: {
                      depositor: metadataFile.depositor['custom:identityid'],
                      deposit_name: metadataFile.name,
                      created_date: metadataFile.createdDate || '',
                    }
                  }"
                >
                  {{ metadataFile.name }}
                </router-link>
                <template v-else>
                  {{ metadataFile.name }}
                </template>
              </td>
              <td>{{ formatDate(metadataFile.createdDate) }}</td>
              <td>{{ metadataFile.status }}</td>
              <td v-if="isAdmin">
                <v-btn
                  icon
                  variant="text"
                  :title="`Edit ${metadataFile.name}`"
                  :disabled="metadataFile.status !== 'open'"
                  @click="openEdit(metadataFile)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
    </v-row>
    <v-row v-else>
      <p>Your Deposit Service account has been successfully activated! Now contact <a href="mailto:digitalpreservationservice@lib.cam.ac.uk">digitalpreservationservice@lib.cam.ac.uk</a> to request that a deposit is set up so you can begin uploading your files.</p>
    </v-row>

    <!-- Edit dialog -->
    <v-dialog
      v-model="editDialog"
      max-width="960"
      @update:model-value="onEditDialogToggle"
    >
      <v-card>
        <v-card-title class="text-h6">
          Edit deposit details
        </v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-model="editFormValid"
            lazy-validation
            class="form-narrow"
          >
            <!-- Name -->
            <div class="field w-80">
              <div class="label-row">
                <span
                  id="lbl-name"
                  class="form-label"
                >Name</span>
                <v-tooltip
                  text="E.g. 'CUL Records'. This will be the 'Title' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Name'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="name"
                v-model="editableDeposit.name"
                :aria-labelledby="'lbl-name'"
                placeholder="Enter name"
                :rules="nameRules"
                :counter="250"
                clearable
                variant="outlined"
                density="comfortable"
              />
            </div>

            <!-- Collecting Area -->
            <div class="field w-80">
              <div class="label-row">
                <span
                  id="lbl-colarea"
                  class="form-label"
                >Collecting Area</span>
                <v-tooltip
                  text="The Collecting Area this Deposit will be assigned to when ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Collecting Area'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-select
                id="collectingArea"
                v-model="editableDeposit.collectingArea"
                :aria-labelledby="'lbl-colarea'"
                :items="collectingAreaOptions"
                placeholder="Select collecting area"
                :rules="collectingAreaRules"
                clearable
                variant="outlined"
                density="comfortable"
              />
            </div>

            <!-- Pair 1: Accession Number OR Identifier -->
            <div class="pair-row">
              <div class="field">
                <div class="label-row">
                  <span
                    id="lbl-accessionNumber"
                    class="form-label"
                  >Accession Number</span>
                  <v-tooltip
                    text="E.g. 'GBR/0265/2025/032'. This will be the 'Accession Number' field of the package when this Deposit is ingested into the repository."
                    content-class="tip-wrap-100"
                  >
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        class="info-icon"
                        icon
                        size="x-small"
                        variant="text"
                        :ripple="false"
                        :aria-label="'Help: Accession Number'"
                      >
                        <v-icon>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </div>

                <v-text-field
                  id="accessionNumber"
                  v-model="editableDeposit.accessionNumber"
                  :aria-labelledby="'lbl-accessionNumber'"
                  class="pair-left"
                  placeholder="Enter accession number"
                  :rules="externalIdRules"
                  :counter="200"
                  clearable
                  variant="outlined"
                  density="comfortable"
                />
              </div>

              <div
                class="or-chip"
                aria-hidden="true"
              >
                OR
              </div>

              <div class="field">
                <div class="label-row">
                  <span
                    id="lbl-identifier"
                    class="form-label"
                  >Identifier</span>
                  <v-tooltip
                    text="E.g. 'GBR/0012/MS SPR/107'. This will be the 'Identifier' field of the package when this Deposit is ingested into the repository."
                    content-class="tip-wrap-100"
                  >
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        class="info-icon"
                        icon
                        size="x-small"
                        variant="text"
                        :ripple="false"
                        :aria-label="'Help: Identifier'"
                      >
                        <v-icon>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </div>

                <v-text-field
                  id="identifier"
                  v-model="editableDeposit.identifier"
                  :aria-labelledby="'lbl-identifier'"
                  class="pair-right"
                  placeholder="Enter identifier"
                  :rules="identifierRules"
                  :counter="200"
                  clearable
                  variant="outlined"
                  density="comfortable"
                />
              </div>
            </div>

            <!-- Pair 2: ArchivesSpace Accession ID OR ArchivesSpace Archival Object ID -->
            <div class="pair-row">
              <div class="field">
                <div class="label-row">
                  <span
                    id="lbl-accessionId"
                    class="form-label"
                  >ArchivesSpace Accession ID</span>
                  <v-tooltip
                    text="E.g. '27510'. You can find this number at the end of the URL to the accession record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/accessions/27510. This will be the 'ID of ArchivesSpace Accession Record' field of the package when this Deposit is ingested into the repository."
                    content-class="tip-wrap-100"
                  >
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        class="info-icon"
                        icon
                        size="x-small"
                        variant="text"
                        :ripple="false"
                        :aria-label="'Help: ArchivesSpace Accession ID'"
                      >
                        <v-icon>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </div>

                <v-text-field
                  id="accessionId"
                  v-model="editableDeposit.accessionId"
                  :aria-labelledby="'lbl-accessionId'"
                  class="pair-left"
                  placeholder="Digits only"
                  :rules="accessionRules"
                  :counter="200"
                  clearable
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  variant="outlined"
                  density="comfortable"
                />
              </div>

              <div
                class="or-chip"
                aria-hidden="true"
              >
                OR
              </div>

              <div class="field">
                <div class="label-row">
                  <span
                    id="lbl-aaoid"
                    class="form-label"
                  >ArchivesSpace Archival Object ID</span>
                  <v-tooltip
                    text="E.g. '1728103'. You can find this number at the end of the URL to the record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/resources/13400#tree::archival_object_1728103. This will be the 'ID of ArchivesSpace Archival Object Record' field of the package when this Deposit is ingested into the repository."
                    content-class="tip-wrap-100"
                  >
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        class="info-icon"
                        icon
                        size="x-small"
                        variant="text"
                        :ripple="false"
                        :aria-label="'Help: ArchivesSpace Archival Object ID'"
                      >
                        <v-icon>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </div>

                <v-text-field
                  id="archivesSpaceArchivalObjectRecordId"
                  v-model="editableDeposit.archivesSpaceArchivalObjectRecordId"
                  :aria-labelledby="'lbl-aaoid'"
                  class="pair-right"
                  placeholder="Enter Archival Object ID"
                  :rules="aaoidRules"
                  :counter="200"
                  clearable
                  variant="outlined"
                  density="comfortable"
                />
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            @click="editDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="secondary"
            :loading="saving"
            :disabled="!isAdmin || !editFormValid || saving || !isDirty"
            @click="saveEdit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import config from "@/config";
import { Auth } from "@/utils/amplify-compat";
import { S3Client } from "@aws-sdk/client-s3";
import { formatDate } from "@/utils/formatDate";
import { updateDepositMetadata } from "@/utils/depositMetadata";
import { ensureAdmin, isAdminFromToken } from '@/utils/adminTools'

export default {
  name: "DepositList",
  props: {
    dialog: Boolean,
    deposits: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  emits: ['refresh'],
  data: () => ({
    isAdmin: false,
    dirs: [
      { name: 'Deposits', id: 1, children: [] }
    ],
    metadata: {},
    activeDeposit: null,

    collectingAreaOptions: [
     'Cambridge University Digital Library',
     'Cultural Heritage Imaging Laboratory',
     'Department of Archives and Modern Manuscripts',
     'Digital Preservation',
     'Map Department',
     'Music Department',
     'Open Research Systems',
     'World Collections',
    ],

    // editing state
    editDialog: false,
    editableDeposit: {
      id: null,
      key: null,
      name: "",
      accessionNumber: "",
      identifier: "",
      accessionId: "",
      archivesSpaceArchivalObjectRecordId: "",
      collectingArea: "Department of Archives and Modern Manuscripts",
    },
    originalSnapshot: null, // for dirty-checking & restore
    rowRef: null,           // direct reference to the row object
    editFormValid: true,
    saving: false,

    // simple rules you can harden later
    nameRules: [
      v => (v === null || v === undefined || v === '' || v.length <= 250) || 'Name must be <= 250 chars'
    ],
  }),
  computed: {
    isDirty() {
      if (!this.originalSnapshot) return false;
      return (
        (this.editableDeposit.name ?? '') !== (this.originalSnapshot.name ?? '') ||
        (this.editableDeposit.accessionNumber ?? '') !== (this.originalSnapshot.accessionNumber ?? '') ||
        (this.editableDeposit.identifier ?? '') !== (this.originalSnapshot.identifier ?? '') ||
        (this.editableDeposit.accessionId ?? '') !== (this.originalSnapshot.accessionId ?? '') ||
        (this.editableDeposit.archivesSpaceArchivalObjectRecordId ?? '') !== (this.originalSnapshot.archivesSpaceArchivalObjectRecordId ?? '')
      );
    },
    externalIdRules() {
      return [
        v => (this.isBlank(v) || String(v).length <= 200) || 'Accession Number must be ≤ 200 chars',
        () => this.accessionVsIdentifierRule(),   // XOR
      ];
    },
    identifierRules() {
      return [
        v => (this.isBlank(v) || String(v).length <= 200) || 'Identifier must be ≤ 200 chars',
        () => this.accessionVsIdentifierRule(),   // XOR
      ];
    },
    accessionRules() {
      return [
        v => (this.isBlank(v) || /^\d+$/.test(String(v))) || 'Must be a whole number',
        () => this.aspaceAccessionVsAOidRule(),   // XOR
      ];
    },
    aaoidRules() {
      return [
        v => (this.isBlank(v) || String(v).length <= 200) || 'ID must be ≤ 200 chars',
        () => this.aspaceAccessionVsAOidRule(),   // XOR
      ];
    },
    collectingAreaRules() {
      return [
        v => (v === '' || v === null || v === undefined || this.collectingAreaOptions.includes(v)) || 'Must be one of the listed options',
      ];
    },
  },
  watch: {
    'editableDeposit.accessionNumber'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.identifier'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.accessionId'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.archivesSpaceArchivalObjectRecordId'() { this.$refs.editForm?.validate?.(); },
  },
  async mounted() {
      try {
        if (this.$cognitoAuth?.getIdToken) {
          this.isAdmin = await ensureAdmin(this.$cognitoAuth, '105877'); // or config.adminGroups
        } else {
          const session = await Auth.currentSession();
          const token = session?.getIdToken?.()?.getJwtToken?.();
          this.isAdmin = !!token && isAdminFromToken(token, '105877');
        }
      } catch {
        this.isAdmin = false;
      }
    },
  methods: {
    formatDate,
    // Applies `update` to the current metadata file (see updateDepositMetadata).
    // Returns the written metadata, or null if the write failed.
    async updateMetadata(objectKey, update) {
      try {
        const credentials = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(credentials),
        });

        return await updateDepositMetadata(client, objectKey, update);
      } catch (err) {
        console.log("Error saving to S3:", err);
        return null;
      }
    },

    openEdit(deposit) {
      if (!this.isAdmin) return;
      this.rowRef = deposit;
      this.editableDeposit = {
        id: deposit.id,
        key: deposit.key,
        name: deposit.name ?? "",
        accessionNumber: deposit.accessionNumber ?? "",
        identifier: deposit.identifier ?? "",
        accessionId: deposit.accessionId ?? "",
        archivesSpaceArchivalObjectRecordId: deposit.archivesSpaceArchivalObjectRecordId ?? "",
        collectingArea: deposit.collectingArea ?? "Department of Archives and Modern Manuscripts",
      };
      // snapshot to support dirty-checking & cancel
      this.originalSnapshot = { 
          ...this.editableDeposit,
          collectingArea: deposit.collectingArea ?? "",   // raw value, not the display default — lets isDirty catch a first-time set
      };
      this.$nextTick(() => {
        this.$refs.editForm?.resetValidation?.();
      });
      this.editDialog = true;
    },

    onEditDialogToggle(val) {
      if (!val) {
        // reset everything on close
        this.rowRef = null;
        this.originalSnapshot = null;
        this.editableDeposit = {
          id: null,
          key: null,
          name: "",
          identifier: "",
          accessionNumber: "",
          accessionId: "",
          collectingArea: "Department of Archives and Modern Manuscripts",
        };
        this.$refs.editForm?.resetValidation?.();
      }
    },

    async saveEdit() {
      if (!this.isAdmin) return;
      if (!this.$refs.editForm?.validate?.()) return;
      if (!this.rowRef) return;

      const err1 = this.xorMessage(
        this.editableDeposit.accessionNumber, this.editableDeposit.identifier,
        'Accession number', 'Identifier'
      );
      const err2 = this.xorMessage(
        this.editableDeposit.accessionId, this.editableDeposit.archivesSpaceArchivalObjectRecordId,
        'ArchivesSpace Accession ID', 'ArchivesSpace Archival Object ID'
      );
      if (err1 || err2) { this.$refs.editForm?.validate?.(); return; }

      // Overwrite only the edited fields of the current metadata file
      this.saving = true;
      const metadata = await this.updateMetadata(this.rowRef.key, (metadata) => {
        metadata.name = this.editableDeposit.name ?? "";
        metadata.accessionNumber = this.editableDeposit.accessionNumber ?? "";
        metadata.identifier = this.editableDeposit.identifier ?? "";
        metadata.accessionId = this.editableDeposit.accessionId ?? "";
        metadata.archivesSpaceArchivalObjectRecordId = this.editableDeposit.archivesSpaceArchivalObjectRecordId ?? "";
      });
      this.saving = false;

      if (metadata) {
        // Optimistic local update so the table reflects changes immediately
        this.rowRef.name = metadata.name;
        this.rowRef.accessionNumber = metadata.accessionNumber;
        this.rowRef.identifier = metadata.identifier;
        this.rowRef.accessionId = metadata.accessionId;
        this.rowRef.archivesSpaceArchivalObjectRecordId = metadata.archivesSpaceArchivalObjectRecordId;

        this.editDialog = false;
        // If you prefer a hard refresh from the source of truth, uncomment:
        this.$emit('refresh');
        console.log('DepositList: refresh emitted');
      } else {
        console.warn("Failed to save updated metadata to S3");
        // Keep dialog open so user can retry
      }
    },

    isBlank(v) {
      return v === null || v === undefined || String(v).trim() === '';
    },
    xorMessage(aVal, bVal, aLabel, bLabel) {
      const aBlank = this.isBlank(aVal);
      const bBlank = this.isBlank(bVal);
      if (aBlank && bBlank) return `Provide ${aLabel} OR ${bLabel}.`;
      if (!aBlank && !bBlank) return `Provide only one of ${aLabel} OR ${bLabel}, not both.`;
      return '';
    },
    accessionVsIdentifierRule() {
      return this.xorMessage(
        this.editableDeposit.accessionNumber,
        this.editableDeposit.identifier,
        'Accession number',
        'Identifier'
      ) || true;
    },
    aspaceAccessionVsAOidRule() {
      return this.xorMessage(
        this.editableDeposit.accessionId,
        this.editableDeposit.archivesSpaceArchivalObjectRecordId,
        'ArchivesSpace Accession ID',
        'ArchivesSpace Archival Object ID'
      ) || true;
    },
  }
};
</script>
<style scoped>
/* External labels + centered info icon */
.label-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin: 0.25rem auto;
  width: 100%;
  text-align: center;
}
.form-label {
  font-weight: 600;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
.info-icon :deep(.v-icon) { opacity: 0.7; }
.info-icon:hover :deep(.v-icon),
.info-icon:focus-visible :deep(.v-icon) { opacity: 1; }

/* Pair layout + OR chip */
.pair-row {
  display: grid;
  grid-template-columns: minmax(0,1fr) auto minmax(0,1fr);
  gap: 0.75rem;
  align-items: start;
  width: 80%;
  margin: 0.75rem auto 0;
}
.w-80 { width: 80%; margin-left: auto; margin-right: auto; }
.pair-left, .pair-right { width: 100%; }
.or-chip {
  align-self: center;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(0,0,0,0.2);
  border-radius: 999px;
  user-select: none;
  opacity: 0.75;
}

/* Tooltips: DO NOT TRUNCATE (works with scoped + teleport) */
:deep(.tip-wrap-100) {
  max-width: 100ch !important;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: normal;
  text-align: left;
  line-height: 1.25;
}
@media (max-width: 800px) {
  .pair-row { grid-template-columns: 1fr; width: 100%; }
  .w-80 { width: 100%; }
  .or-chip { display: none; }
}
@media (max-width: 600px) {
  :deep(.tip-wrap-100) { max-width: 90vw !important; }
}
</style>
